import type {
  AcquireAttempt,
  AiringSlot,
  ApiResponse,
  ContinueCard,
  EpisodeAvailability,
  LibraryEntry,
  LibraryStatus,
  MediaProgress,
  MediaState,
  MediaWarning,
  NewEpisodeCard,
  RejectionReasonCode,
  RelatedTitle,
  TitleBrief,
  TitleFull,
} from '@anisync/types';

/**
 * Klient API. Rozpakowuje kopertę {ok,data}/{ok,error} w jednym miejscu, więc
 * wywołania w komponentach dostają albo dane, albo wyjątek z kodem — nigdy
 * nie muszą sprawdzać `res.ok` ręcznie.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    // Sesja i tożsamość gościa jadą w httpOnly cookie — tokeny nigdy nie
    // trafiają do JS, więc każde żądanie musi je dołączać jawnie.
    credentials: 'same-origin',
    /**
     * `...init` PRZED `headers`, nie po.
     *
     * Odwrotna kolejność wyglądała poprawnie i była pułapką: rozwinięcie `init`
     * na końcu podmieniało cały scalony obiekt nagłówków, więc wywołanie
     * podające własny nagłówek gubiło `Accept` i domyślny typ treści. Nie
     * bolało, dopóki nikt nie podawał nagłówków — pierwszym takim wywołaniem
     * jest import listy z MAL, który wysyła surowe bajty.
     */
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  let body: ApiResponse<T> | null = null;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    // Nie-JSON oznacza zwykle, że odpowiedział proxy, a nie aplikacja.
    throw new ApiError(
      'INTERNAL',
      `Serwer zwrócił odpowiedź, której nie da się odczytać (HTTP ${res.status})`,
      res.status,
    );
  }

  if (!body.ok) {
    throw new ApiError(body.error.code, body.error.message, res.status, body.error.detail);
  }
  return body.data;
}

export interface HealthPayload {
  version: string;
  env: string;
  uptimeSec: number;
  db: { ok: boolean; detail?: string };
  disk: { ok: boolean; freeBytes?: number; totalBytes?: number; belowMinimum?: boolean };
  features: {
    oidc: boolean;
    guestBrowse: boolean;
    guestUpload: boolean;
    egress: string[];
    aniskip: boolean;
    animesub: boolean;
  };
  mediaTtlDays: number;
}

export function getHealth(): Promise<HealthPayload> {
  return apiFetch<HealthPayload>('/health');
}

export function searchTitles(query: string, limit = 24): Promise<TitleBrief[]> {
  return apiFetch<TitleBrief[]>(`/catalogue/search?q=${encodeURIComponent(query)}&limit=${limit}`);
}

export function recentTitles(limit = 24): Promise<TitleBrief[]> {
  return apiFetch<TitleBrief[]>(`/catalogue/recent?limit=${limit}`);
}

/**
 * Tytuł razem z powiązaniami.
 *
 * Serwer dokłada je do tej samej odpowiedzi, bo strona tytułu potrzebuje ich
 * zawsze — osobne żądanie znaczyłoby drugi moment, w którym układ podskakuje.
 */
export function getTitle(titleId: string): Promise<TitleFull & { relations: RelatedTitle[] }> {
  return apiFetch<TitleFull & { relations: RelatedTitle[] }>(`/catalogue/titles/${titleId}`);
}

export function getEpisodes(titleId: string): Promise<EpisodeAvailability[]> {
  return apiFetch<EpisodeAvailability[]>(`/catalogue/titles/${titleId}/episodes`);
}

export interface SourceListItem {
  descriptor: {
    id: string;
    label: string;
    homepage: string;
    playerHosts: string[];
    capabilities: Record<string, boolean>;
    revision: number;
  };
  enabled: boolean;
  priority: number;
  revisionStale: boolean;
  health: {
    status: 'ok' | 'degraded' | 'down';
    checkedAt: string | null;
    latencyMs: number | null;
    detail: string | null;
    selectorMissRate: number;
  } | null;
}

export function getSources(): Promise<{ reviewQueue: number; sources: SourceListItem[] }> {
  return apiFetch('/sources');
}

export interface PlayStart {
  mediaFileId: string;
  state: MediaState;
  deduped: boolean;
  streamUrl: string | null;
}

/**
 * Wersja odcinka do wyboru przed pobraniem.
 *
 * `translation: null` znaczy „serwis nie mówi", a nie „napisy" — tego nie
 * zgadujemy, bo pole wypełniają dwa źródła z pięciu.
 */
export interface VersionOption {
  /** Adres odtwarzacza — jedyny klucz, który jednoznacznie wskazuje wersję. */
  embedUrl: string;
  sourceId: string;
  sourceLabel: string;
  host: string;
  playerLabel: string;
  language: 'pl' | 'en';
  fallback: boolean;
  translation: 'dub' | 'subs' | null;
  quality: string | null;
  /** Napisy z tłumaczenia maszynowego, gdy źródło mówi to wprost. */
  machineTranslated: boolean;
  /** Język napisów, gdy źródło podaje go wprost (dziś tylko Shinden). */
  subsLang: string | null;
  /** Odrzucenie przyciskiem „Zła wersja” — `null`, gdy kopia nie była odrzucana. */
  rejected: { id: string; reason: RejectionReasonCode } | null;
  score: number;
  viaEgress: boolean;
}

export function getEpisodeVersions(
  titleId: string,
  episode: number,
): Promise<{ items: VersionOption[] }> {
  return apiFetch(`/play/versions?titleId=${titleId}&episode=${episode}`);
}

/**
 * Uruchamia odcinek. Bez wskazań serwer wybiera sam, przedkładając napisy nad
 * lektor — z listy wersji przychodzi tu `sourceId` i `preferEmbedUrl`.
 */
export function startPlay(
  titleId: string,
  episode: number,
  prefer?: {
    sourceId?: string;
    preferHost?: string;
    preferEmbedUrl?: string;
    preferAudio?: 'subs' | 'dub' | 'any';
  },
): Promise<PlayStart> {
  return apiFetch<PlayStart>('/play', {
    method: 'POST',
    body: JSON.stringify({ titleId, episode, ...prefer }),
  });
}

export interface PlayStatus extends MediaProgress {
  errorCode: string | null;
  durationSec: number | null;
  /** Rozmiar gotowego pliku — z niego i z czasu trwania liczymy wymagane pasmo. */
  sizeBytes: number | null;
  playability: string | null;
  streamUrl: string | null;
  /** Ostrzeżenia o gotowym materiale. Brak pola = materiał jeszcze niegotowy. */
  warnings?: MediaWarning[];
  /** Czy da się go odrzucić jako złą wersję (znamy kopię, z której pochodzi). */
  rejectable?: boolean;
}

export function getPlayStatus(mediaFileId: string): Promise<PlayStatus> {
  return apiFetch<PlayStatus>(`/play/${mediaFileId}/status`);
}

export interface BatchProgressItem {
  mediaFileId: string;
  state: MediaState;
  errorCode: string | null;
  bytesDone: number | null;
  bytesTotal: number | null;
  speedBps: number | null;
  etaSec: number | null;
  stage: string | null;
  stagePercent: number | null;
  /** `true` znaczy „w kolejce": pobieramy jeden plik naraz. */
  waiting: boolean;
}

/**
 * Postęp wielu materiałów jednym żądaniem.
 *
 * Siatka odcinków przy „przygotuj całą serię" pyta o dwadzieścia pozycji naraz —
 * dwadzieścia osobnych żądań na takt byłoby ruchem bez treści.
 */
export function getBatchProgress(ids: string[]): Promise<{ items: BatchProgressItem[] }> {
  return apiFetch(`/play/progress?ids=${ids.join(',')}`);
}

export interface PlayAttempts {
  attempts: AcquireAttempt[];
  chosenHost: string | null;
  viaEgress: boolean;
  jobs: Array<{
    kind: string;
    status: string;
    progress: number;
    error: string | null;
    startedAt: string | null;
    finishedAt: string | null;
  }>;
}

export function getPlayAttempts(mediaFileId: string): Promise<PlayAttempts> {
  return apiFetch<PlayAttempts>(`/play/${mediaFileId}/attempts`);
}

export interface QueueItem {
  mediaFileId: string;
  titleId: string | null;
  label: string;
  episodeNumber: number | null;
  state: MediaState;
  errorCode: string | null;
  createdAt: string;
  /** Miejsce w kolejce od 1, albo `null`, gdy materiał nie czeka na slot. */
  position: number | null;
  progress: MediaProgress | null;
}

export interface QueueView {
  items: QueueItem[];
  slots: { running: number; waiting: number; limit: number; mbps: number };
  /** Ile pobrań przed nami należy do innych kont — sama liczba, bez tytułów. */
  othersWaiting: number;
  /** Odcinki paczek czekające w kolejce serii — pobierane po jednym. */
  seriesWaiting: number;
}

/**
 * Kolejka pobierań tego konta.
 *
 * Osobno od `getMediaList`, bo to inny cykl życia: biblioteka to rzeczy, które są,
 * a kolejka to praca w toku, odświeżana co parę sekund. Doklejenie jednego do
 * drugiego kazałoby ekranowi plików odpytywać się przez cały czas otwarcia.
 */
export function getDownloadQueue(): Promise<QueueView> {
  return apiFetch<QueueView>('/play/queue');
}

export function retryPlay(mediaFileId: string, preferHost?: string): Promise<PlayStart> {
  return apiFetch<PlayStart>(`/play/${mediaFileId}/retry`, {
    method: 'POST',
    body: JSON.stringify(preferHost !== undefined ? { preferHost } : {}),
  });
}

/* ----------------------------- uwierzytelnianie ----------------------------- */

export interface OidcConfig {
  enabled: boolean;
  providerName: string;
  loginUrl: string;
}

export interface ProfileInfo {
  id: string;
  name: string;
  avatarUrl: string | null;
  createdAt: string;
  /** `null` znaczy „jak konto", nie „wyłączone". */
  prefs: { autoplayNext: boolean | null; autoSkipOpening: boolean | null };
}

export interface SessionInfo {
  authenticated: boolean;
  user: {
    id: string;
    username: string;
    displayName: string | null;
    email: string | null;
    isAdmin: boolean;
    groups: string[];
  } | null;
  /**
   * Profile konta i ten aktywny — **w tej samej odpowiedzi co sesja**.
   *
   * Osobne żądanie znaczyłoby, że nazwa profilu w nagłówku doskakuje po chwili;
   * to ten sam defekt, który przy `useSession()` sprawiał, że „wczytuję" wyglądało
   * jak „nie jesteś zalogowany".
   */
  profiles?: ProfileInfo[];
  activeProfileId?: string;
}

export function listProfiles(): Promise<{ items: ProfileInfo[] }> {
  return apiFetch('/profiles');
}

export function createProfile(name: string): Promise<ProfileInfo> {
  return apiFetch('/profiles', { method: 'POST', body: JSON.stringify({ name }) });
}

export function patchProfile(
  id: string,
  change: {
    name?: string;
    prefs?: { autoplayNext?: boolean | null; autoSkipOpening?: boolean | null };
  },
): Promise<ProfileInfo> {
  return apiFetch(`/profiles/${id}`, { method: 'PATCH', body: JSON.stringify(change) });
}

export function deleteProfile(id: string): Promise<{ deleted: boolean }> {
  return apiFetch(`/profiles/${id}`, { method: 'DELETE' });
}

/** Przypina profil do sesji; po tym trzeba odświeżyć sesję i dane ekranu. */
export function switchProfile(id: string): Promise<ProfileInfo> {
  return apiFetch(`/profiles/${id}/use`, { method: 'POST' });
}

export function getOidcConfig(): Promise<OidcConfig> {
  return apiFetch<OidcConfig>('/auth/oidc/config');
}

export function getSession(): Promise<SessionInfo> {
  return apiFetch<SessionInfo>('/auth/me');
}

export function logout(): Promise<{ loggedOut: boolean; providerLogoutUrl: string | null }> {
  return apiFetch('/auth/logout', { method: 'POST' });
}

/* -------------------------------- biblioteka -------------------------------- */

export function getLibrary(status?: LibraryStatus): Promise<{ items: LibraryEntry[] }> {
  const query = status !== undefined ? `?status=${status}` : '';
  return apiFetch(`/library${query}`);
}

export function getContinueWatching(limit = 20): Promise<{ items: ContinueCard[] }> {
  return apiFetch(`/library/continue?limit=${limit}`);
}

export function getNewEpisodes(limit = 12): Promise<{ items: NewEpisodeCard[] }> {
  return apiFetch(`/library/new-episodes?limit=${limit}`);
}

export function setLibraryEntry(
  titleId: string,
  update: { status?: LibraryStatus; score?: number | null; favourite?: boolean },
): Promise<{ entry: LibraryEntry }> {
  return apiFetch(`/library/${titleId}`, { method: 'PUT', body: JSON.stringify(update) });
}

export function getTitleProgress(titleId: string): Promise<{
  episodes: Array<{
    episodeNumber: number;
    positionSec: number;
    durationSec: number;
    completed: boolean;
    updatedAt: string;
  }>;
}> {
  return apiFetch(`/library/${titleId}/progress`);
}

export interface ProgressReport {
  titleId: string;
  episodeNumber: number;
  positionSec: number;
  durationSec: number;
}

export function reportProgress(report: ProgressReport): Promise<{
  saved: boolean;
  completed: boolean;
  libraryStatus: LibraryStatus | null;
}> {
  return apiFetch('/progress', { method: 'PATCH', body: JSON.stringify(report) });
}

/**
 * Ostatni zapis przy zamykaniu karty.
 *
 * `sendBeacon` jest tu jedyną działającą opcją: zwykły `fetch` z zamykanej karty
 * jest przerywany, a przeglądarka nie gwarantuje jego dostarczenia. Ograniczenie
 * jest takie, że beacon nie czyta odpowiedzi — więc nie dowiemy się, czy zapis
 * się udał, i dlatego to zawsze dopełnienie regularnego raportowania, nie
 * zamiennik.
 */
export function beaconProgress(report: ProgressReport): boolean {
  if (typeof navigator.sendBeacon !== 'function') return false;
  // PATCH nie jest dostępny dla beacona (zawsze POST), więc serwer wystawia
  // ten sam zapis pod POST /progress/beacon.
  return navigator.sendBeacon(
    '/api/v1/progress/beacon',
    new Blob([JSON.stringify(report)], { type: 'application/json' }),
  );
}

/* -------------------------------- przekaźnik -------------------------------- */

export interface RelayAccepted {
  manifestKind: string;
  height: number | null;
}

/**
 * Ręczne dostarczenie strony odtwarzacza albo gotowego manifestu.
 *
 * Droga zawsze dostępna, także bez skryptu przeglądarkowego — serwer nie może
 * pobrać tej strony sam, bo host blokuje jego numer AS.
 */
/**
 * Czy serwer słyszał ostatnio przekaźnik.
 *
 * To jest stan rozstrzygający, a nie obecność skryptu w tej karcie: kaskada
 * opiera na nim decyzję, czy warto czekać na rozwiązanie strony cda.
 */
export function getRelayStatus(): Promise<{ available: boolean }> {
  return apiFetch('/relay/status');
}

/** Stan logowania w serwisie, z którego przekaźnik przynosi strony z sesją człowieka. */
export interface RelaySession {
  site: 'anime-odcinki';
  label: string;
  loginUrl: string;
  state: 'logged-in' | 'logged-out' | 'no-relay' | 'relay-silent' | 'unknown';
  checkedAt: string | null;
  detail: string | null;
}

export function getRelaySessions(): Promise<{ items: RelaySession[] }> {
  return apiFetch('/relay/sessions');
}

/** Każe przeglądarce z przekaźnikiem przynieść stronę próbną; trwa do ~25 s. */
export function checkRelaySession(site: RelaySession['site']): Promise<RelaySession> {
  return apiFetch(`/relay/sessions/${site}/check`, { method: 'POST' });
}

export function submitRelayManual(input: {
  pageUrl: string;
  html?: string;
  manifestUrl?: string;
}): Promise<RelayAccepted> {
  return apiFetch<RelayAccepted>('/relay/manual', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Odtworzenie wskazanego linku cda, bez kontekstu katalogu.
 *
 * Osobna operacja od `startPlay`, bo tam identyfikacja idzie po tytule
 * i numerze odcinka, a tutaj mamy sam adres strony.
 */
export function playCdaLink(pageUrl: string): Promise<PlayStart> {
  return apiFetch<PlayStart>('/play/link', {
    method: 'POST',
    body: JSON.stringify({ pageUrl }),
  });
}

/* ---------------------------------- pokoje ---------------------------------- */

export function createRoom(input: {
  name: string;
  isPublic?: boolean;
  /**
   * Wróć do własnego pustego pokoju o tej nazwie zamiast zakładać kolejny.
   *
   * Dla „Razem" przy odcinku, nie dla „Nowy pokój": ten drugi ma tworzyć nowy.
   * `reused` w odpowiedzi mówi, co się stało — bez tego nie da się napisać
   * uczciwego komunikatu.
   */
  reuseEmpty?: boolean;
}): Promise<{ slug: string; id: string; reused: boolean }> {
  return apiFetch('/rooms', { method: 'POST', body: JSON.stringify(input) });
}

export function addToRoomQueue(
  slug: string,
  input: { titleId?: string; episodeNumber?: number; mediaFileId?: string },
): Promise<{ uid: string; slug: string }> {
  return apiFetch(`/rooms/${slug}/queue`, { method: 'POST', body: JSON.stringify(input) });
}

export interface RoomListItem {
  slug: string;
  name: string;
  isTemporary: boolean;
  expiresAt: string | null;
  /** Ilu ludzi jest w pokoju TERAZ. Zero znaczy „pusty", a nie „brak danych". */
  present: number;
  /**
   * Kiedy pusty pokój zostanie usunięty; `null` znaczy „nie zostanie".
   *
   * Sprzątanie zabiera razem z pokojem kolejkę i czat, więc termin ma być widoczny
   * PRZED jego nadejściem, a nie tłumaczony po fakcie.
   */
  idleDeleteAt: string | null;
}

export function getMyRooms(): Promise<{ items: RoomListItem[] }> {
  return apiFetch('/rooms');
}

export function createRoomInvite(
  slug: string,
  input: { role?: 'viewer' | 'controller'; maxUses?: number; ttlHours?: number } = {},
): Promise<{ code: string; role: string; expiresAt: string | null; url: string }> {
  return apiFetch(`/rooms/${slug}/invites`, { method: 'POST', body: JSON.stringify(input) });
}

export function markWatched(
  titleId: string,
  episode: number,
  watched: boolean,
): Promise<{ titleId: string; episodeNumber: number; watched: boolean }> {
  return apiFetch(`/progress/${titleId}/episodes/${episode}/watched`, {
    method: 'POST',
    body: JSON.stringify({ watched }),
  });
}

/* --------------------------- napisy i pomijanie --------------------------- */

export interface SubtitleTrack {
  id: string;
  lang: string;
  format: 'ass' | 'srt' | 'vtt';
  label: string | null;
  origin: string;
  sizeBytes: number | null;
  /** Adres pliku — gotowy do podania rendererowi. */
  url: string;
}

export interface SkipRange {
  kind: 'op' | 'ed' | 'recap' | 'preview';
  startSec: number;
  endSec: number;
  source: string;
}

export function getSubtitleTracks(mediaFileId: string): Promise<{ tracks: SubtitleTrack[] }> {
  return apiFetch(`/media/${mediaFileId}/subtitles`);
}

export function getSkipRanges(
  titleId: string,
  episode: number,
  /**
   * Czas trwania, jeśli znany. AniSkip odrzuca po nim zgłoszenia dotyczące innego
   * wydania — bez tej liczby musielibyśmy przyjmować każdy zakres na wiarę.
   */
  lengthSec?: number | null,
): Promise<{ ranges: SkipRange[] }> {
  const length =
    typeof lengthSec === 'number' && Number.isFinite(lengthSec) && lengthSec > 0
      ? `&lengthSec=${Math.round(lengthSec)}`
      : '';
  return apiFetch(`/skip?titleId=${titleId}&episode=${episode}${length}`);
}

/* --------------------------------- uploady --------------------------------- */

export interface UploadPrecheck {
  /** Obecne, gdy identyczna treść już leży u nas — wtedy nie ma czego wysyłać. */
  mediaFileId: string | null;
  duplicate: boolean;
}

/**
 * Sprawdza limity PRZED wysłaniem pliku.
 *
 * Skrótu nie podajemy: `crypto.subtle` nie liczy przyrostowo, więc sha256
 * kilkugigabajtowego pliku w karcie wymagałoby wczytania go całego do pamięci.
 * Deduplikację rozstrzyga serwer po transferze — kosztuje to transfer w rzadkim
 * przypadku wgrania tej samej treści dwa razy.
 */
export function precheckUpload(input: {
  sizeBytes: number;
  fileName: string;
}): Promise<UploadPrecheck> {
  return apiFetch('/uploads/precheck', { method: 'POST', body: JSON.stringify(input) });
}

/* ------------------------------ import z MAL ------------------------------ */

export interface MalPlanRow {
  malId: number;
  malTitle: string;
  titleId: string | null;
  canonicalTitle: string | null;
  action: 'add' | 'update' | 'unchanged';
  status: LibraryStatus;
  watchedEpisodes: number;
  score: number | null;
  changes: Array<{ field: string; from: string | null; to: string | null }>;
}

export interface MalImportPlan {
  runId: string;
  userName: string | null;
  rows: MalPlanRow[];
  unmatched: Array<{ malId: number; title: string; reason: string }>;
  skipped: Array<{ reason: string; snippet: string }>;
  counts: { add: number; update: number; unchanged: number; unmatched: number; skipped: number };
}

/**
 * Wysyła plik listy i dostaje PLAN, nie wynik.
 *
 * Ciało to surowe bajty pliku: eksport MAL przychodzi spakowany gzipem, a serwer
 * rozpoznaje to po sygnaturze. Zawijanie w formularz wieloczęściowy nic tu nie
 * wnosi, bo wysyłamy dokładnie jeden plik i żadnych pól obok.
 */
export function planMalImport(file: File): Promise<MalImportPlan> {
  return apiFetch('/integrations/mal/import', {
    method: 'POST',
    body: file,
    headers: { 'Content-Type': 'application/octet-stream' },
  });
}

export function applyMalImport(
  runId: string,
  accept: number[],
): Promise<{ applied: number; skipped: number }> {
  return apiFetch('/integrations/mal/import/apply', {
    method: 'POST',
    body: JSON.stringify({ runId, accept }),
  });
}

/* ------------------------------ strona główna ------------------------------ */

export interface HomeSections {
  hero: TitleBrief[];
  airing: TitleBrief[];
  season: TitleBrief[];
  topRated: TitleBrief[];
  recent: TitleBrief[];
}

/**
 * Wszystkie sekcje strony głównej jednym żądaniem.
 *
 * Pięć osobnych wywołań to pięć podróży i pięć momentów, w których układ może
 * podskoczyć — a strona główna potrzebuje wszystkiego naraz.
 */
export function getHome(): Promise<HomeSections> {
  return apiFetch('/catalogue/home');
}

/* ------------------------------- moje pliki ------------------------------- */

export interface MediaListItem {
  id: string;
  kind: 'upload' | 'fetched';
  originalName: string | null;
  titleId: string | null;
  /**
   * Nazwa serii z katalogu — bez niej wiersz pobranego odcinka nazywał się
   * „Odcinek 3" i przy dwóch seriach nie było jak je rozróżnić.
   */
  titleRomaji: string | null;
  titleEnglish: string | null;
  titleCoverUrl: string | null;
  titleEpisodesTotal: number | null;
  episodeNumber: number | null;
  state: MediaState;
  errorCode: string | null;
  durationSec: number | null;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  playability: string | null;
  pinned: boolean;
  expiresAt: string | null;
  createdAt: string;
  posterUrl: string | null;
}

export interface MediaUsage {
  bytes: number;
  files: number;
  /** Limit na konto. Był niewidoczny do chwili odmowy uploadu. */
  quotaBytes: number;
}

export interface MediaListPage {
  items: MediaListItem[];
  nextBefore: string | null;
  hasMore: boolean;
  usage: MediaUsage;
}

export function getMediaList(
  opts: { before?: string; kind?: 'upload' | 'fetched'; limit?: number } = {},
): Promise<MediaListPage> {
  const query = new URLSearchParams();
  if (opts.before !== undefined) query.set('before', opts.before);
  if (opts.kind !== undefined) query.set('kind', opts.kind);
  if (opts.limit !== undefined) query.set('limit', String(opts.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return apiFetch(`/media${suffix}`);
}

/**
 * Usunięcie całego katalogu serii jednym żądaniem.
 *
 * Zwraca rozbicie, bo materiał odtwarzany w pokoju zostaje — „usunięto 23 z 25"
 * musi dać się wyjaśnić, a nie wyglądać na awarię.
 */
export function bulkDeleteMedia(ids: string[]): Promise<{
  deleted: string[];
  inUse: string[];
  missing: number;
}> {
  return apiFetch('/media/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

/**
 * Przypięcie materiału albo własny czas życia.
 *
 * Przypięcie jest jedyną odpowiedzią na plakietkę „zostaje N dni": bez niego
 * informacja o terminie mówiła o czymś, na co nie dało się zareagować.
 */
export function patchMedia(
  mediaFileId: string,
  patch: { pinned?: boolean; ttlDays?: number },
): Promise<{ id: string; pinned: boolean; expiresAt: string | null }> {
  return apiFetch(`/media/${mediaFileId}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteMedia(mediaFileId: string): Promise<{ deleted: boolean; id: string }> {
  return apiFetch(`/media/${mediaFileId}`, { method: 'DELETE' });
}

export interface RejectResult {
  rejectionId: string;
  /**
   * `true`, gdy plik został, bo odtwarza go zajęty pokój — odrzucenie i tak jest
   * zapisane, więc następne pobranie tego odcinka już tej kopii nie weźmie.
   */
  kept: boolean;
  /** Nowy materiał (pobieranie następnej kopii) albo ten sam, gdy `kept`. */
  mediaFileId: string;
  state: MediaState;
}

/**
 * „Zła wersja”: zapamiętaj kopię jako złą, usuń plik i pobierz następną.
 * Odrzucenie działa dla wszystkich widzów instancji.
 */
export function rejectMedia(
  mediaFileId: string,
  reason: RejectionReasonCode,
): Promise<RejectResult> {
  return apiFetch<RejectResult>(`/media/${mediaFileId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/** Cofnięcie odrzucenia — kopia wraca do kaskady dla wszystkich. */
export function restoreVersion(rejectionId: string): Promise<{ restored: boolean }> {
  return apiFetch(`/play/versions/rejections/${rejectionId}`, { method: 'DELETE' });
}

/**
 * Przygotowanie całej serii.
 *
 * Zwraca, co poszło do kolejki i co pominięto — pominięte to odcinki już
 * pobrane, w drodze albo bez żadnego źródła.
 */
export function prepareSeries(
  titleId: string,
  episodes?: number[],
): Promise<{ batchId: string; queued: number[]; skipped: number[] }> {
  return apiFetch(`/catalogue/titles/${titleId}/prepare`, {
    method: 'POST',
    body: JSON.stringify(episodes === undefined ? {} : { episodes }),
  });
}

export function getSchedule(days = 7): Promise<{ days: number; slots: AiringSlot[] }> {
  return apiFetch(`/catalogue/schedule?dni=${days}`);
}

/* -------------------------------- mapowania -------------------------------- */

export interface MappingRow {
  id: string;
  sourceId: string;
  sourceLabel: string;
  providerLabel: string;
  url: string | null;
  titleId: string | null;
  titleRomaji: string | null;
  matchConfidence: number | null;
  matchReason: string | null;
  episodesHint: number | null;
}

export function getMappings(
  status: 'review' | 'unmatched',
  limit = 50,
): Promise<{ items: MappingRow[]; total: number }> {
  return apiFetch(`/catalogue/mappings?status=${status}&limit=${limit}`);
}

/**
 * Kandydat do przypięcia — pozycja w serwisie, którą można wskazać jako źródło
 * tego tytułu. `providerTitleId: null` znaczy „serwis to ma, my jeszcze nie",
 * czyli przypięcie utworzy wpis.
 */
export interface MapCandidate {
  sourceId: string;
  sourceLabel: string;
  language: 'pl' | 'en';
  fallback: boolean;
  slug: string;
  label: string;
  url: string | null;
  providerTitleId: string | null;
  mappedTitleId: string | null;
}

export function getMapCandidates(query: string): Promise<{ items: MapCandidate[] }> {
  return apiFetch(`/catalogue/map-candidates?q=${encodeURIComponent(query)}`);
}

/** Obecne wskazania na ten tytuł — lista z możliwością odpięcia. */
export function getTitleMappings(titleId: string): Promise<{ items: MappingRow[] }> {
  return apiFetch(`/catalogue/titles/${titleId}/mappings`);
}

/**
 * Przypina kandydata do tytułu, tworząc wpis, jeśli serwis znał tę pozycję,
 * a my jeszcze nie.
 */
export function mapCandidate(
  titleId: string,
  candidate: { sourceId: string; slug: string; label: string; url: string | null },
): Promise<{ item: MappingRow }> {
  return apiFetch(`/catalogue/titles/${titleId}/mappings`, {
    method: 'POST',
    body: JSON.stringify(candidate),
  });
}

/**
 * Zapisuje decyzję o mapowaniu. `null` znaczy jawne „nie ma odpowiednika" —
 * pełnoprawną odpowiedź, która blokuje pozycję przed ponownym pytaniem,
 * a zarazem **odpięcie**: złe wskazanie da się usunąć, nie tylko nadpisać.
 */
export function setMapping(
  id: string,
  titleId: string | null,
): Promise<{ id: string; titleId: string | null }> {
  return apiFetch(`/catalogue/mappings/${id}`, {
    method: 'POST',
    body: JSON.stringify({ titleId }),
  });
}

/* ------------------------------- powiadomienia ------------------------------- */

export interface NotificationItem {
  id: string;
  kind: 'media-ready' | 'media-failed' | 'media-expiring' | 'episode-new';
  title: string;
  body: string | null;
  href: string | null;
  read: boolean;
  createdAt: string;
}

export function getNotifications(): Promise<{ items: NotificationItem[]; unread: number }> {
  return apiFetch('/notifications');
}

/** Bez listy identyfikatorów oznacza wszystkie nieprzeczytane. */
export function markNotificationsRead(ids?: string[]): Promise<{ changed: number }> {
  return apiFetch('/notifications/read', {
    method: 'POST',
    body: JSON.stringify(ids === undefined ? {} : { ids }),
  });
}

/* --------------------------------- administracja --------------------------------- */

export interface SettingRow {
  key: string;
  value: number;
  /** Wartość z konfiguracji hosta — widać, wobec czego zmieniono. */
  fallback: number;
  overridden: boolean;
  label: string;
  hint: string;
  min: number;
  max: number;
}

export function getSettings(): Promise<{ items: SettingRow[] }> {
  return apiFetch('/admin/settings');
}

/**
 * Przeładowanie konfiguracji bez restartu usługi.
 *
 * Zrzuca pamięć podręczną ustawień na serwerze i oddaje świeżą listę. Nie dotyczy
 * env: port, ścieżki i sekrety należą do startu procesu i ich podmiana w locie
 * odebrałaby ważność sesjom w środku seansu.
 */
export function reloadSettings(): Promise<{ dropped: number; items: SettingRow[] }> {
  return apiFetch('/admin/settings/reload', { method: 'POST' });
}

export function patchSetting(key: string, value: number): Promise<{ key: string; value: number }> {
  return apiFetch(`/admin/settings/${key}`, {
    method: 'PATCH',
    body: JSON.stringify({ value }),
  });
}

/** Przywraca wartość z konfiguracji — usuwa nadpisanie, nie wpisuje liczby. */
export function resetSetting(key: string): Promise<{ key: string; value: number }> {
  return apiFetch(`/admin/settings/${key}`, { method: 'DELETE' });
}

export interface AuditRow {
  id: string;
  actorLabel: string | null;
  actorKind: 'user' | 'guest' | 'system';
  action: string;
  target: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
}

export function getAudit(
  opts: { before?: string; action?: string; limit?: number } = {},
): Promise<{ items: AuditRow[]; nextBefore: string | null; hasMore: boolean }> {
  const query = new URLSearchParams();
  if (opts.before !== undefined) query.set('before', opts.before);
  if (opts.action !== undefined) query.set('action', opts.action);
  if (opts.limit !== undefined) query.set('limit', String(opts.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return apiFetch(`/admin/audit${suffix}`);
}

export interface DiagnosticsOverview {
  disk: {
    ok: boolean;
    freeBytes: number | null;
    totalBytes: number | null;
    /** Poniżej tej granicy serwer odmawia pobierania i uploadu. */
    minFreeBytes: number;
    belowMinimum: boolean;
  };
  media: {
    byState: Array<{ state: string; count: number; bytes: number }>;
    totalBytes: number;
    totalQuotaBytes: number;
  };
  slots: { running: number; waiting: number; limit: number; mbps: number };
  jobs: Array<{ status: string; count: number }>;
  cache: { kv: number; episodes: number; resolutions: number; expired: number };
  backups: {
    count: number;
    bytes: number;
    /** `null` znaczy „nie ma ani jednej kopii". */
    newestAt: string | null;
    enabled: boolean;
  };
}

export function getDiagnostics(): Promise<DiagnosticsOverview> {
  return apiFetch('/admin/overview');
}

export interface JobRow {
  id: string;
  mediaFileId: string;
  kind: string;
  status: string;
  progress: number;
  attempts: number;
  error: string | null;
  /** Ogon wyjścia narzędzia — przy porażce ffmpega konkret jest na końcu. */
  logTail: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  label: string | null;
  titleRomaji: string | null;
  episodeNumber: number | null;
}

export function getJobs(
  opts: { before?: string; status?: string; limit?: number } = {},
): Promise<{ items: JobRow[]; nextBefore: string | null; hasMore: boolean }> {
  const query = new URLSearchParams();
  if (opts.before !== undefined) query.set('before', opts.before);
  if (opts.status !== undefined) query.set('status', opts.status);
  if (opts.limit !== undefined) query.set('limit', String(opts.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return apiFetch(`/admin/jobs${suffix}`);
}

export function purgeCache(): Promise<{ kv: number; episodes: number; resolutions: number }> {
  return apiFetch('/admin/cache/purge', { method: 'POST' });
}

/* --------------------------- przeglądanie i statystyki --------------------------- */

export interface BrowseParams {
  season?: 'WINTER' | 'SPRING' | 'SUMMER' | 'FALL';
  year?: number;
  genre?: string;
  format?: 'TV' | 'TV_SHORT' | 'MOVIE' | 'OVA' | 'ONA' | 'SPECIAL';
  sort?: 'POPULARITY_DESC' | 'SCORE_DESC' | 'START_DATE_DESC' | 'TRENDING_DESC';
  page?: number;
}

export interface BrowseResult {
  items: TitleBrief[];
  genres: string[];
  /**
   * Niepuste znaczy „to nie jest pełna odpowiedź".
   *
   * Serwer wstawia tu zdanie, gdy AniList ogranicza tempo i wynik pochodzi
   * z lokalnej kopii katalogu — wtedy brakuje tytułów, których nikt jeszcze nie
   * przeglądał, a porządek jest po ocenie zamiast po wybranym sortowaniu.
   */
  notice: string | null;
}

export function browseTitles(params: BrowseParams = {}): Promise<BrowseResult> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return apiFetch(`/catalogue/browse${suffix}`);
}

export interface WatchStats {
  episodes: { completed: number; started: number };
  /** Sekundy realnie odtworzone, nie liczba odcinków razy długość z metadanych. */
  seconds: number;
  titles: { watched: number; completed: number };
  byGenre: Array<{ genre: string; episodes: number }>;
  topTitles: Array<{ titleId: string; romaji: string; episodes: number; seconds: number }>;
  byMonth: Array<{ month: string; episodes: number }>;
  longestStreakDays: number;
  currentStreakDays: number;
  recent: Array<{
    titleId: string;
    romaji: string;
    episodeNumber: number;
    watchedAt: string;
  }>;
}

export function getStats(): Promise<WatchStats> {
  return apiFetch('/stats');
}

export interface UserPrefs {
  /** Po zakończeniu odcinka przejdź do następnego. */
  autoplayNext: boolean;
  /** Pomiń opening sam, gdy znamy jego zakres. */
  autoSkipOpening: boolean;
  /** Własny czas życia pobranych plików; `null` znaczy „jak instancja". */
  mediaTtlDays: number | null;
}

export function getPrefs(): Promise<UserPrefs> {
  return apiFetch('/me/prefs');
}

/**
 * Zmienia wybrane pola. Serwer scala je z resztą, więc ekran wysyła jedno pole
 * po przełączeniu, a nie cały zestaw.
 */
export function patchPrefs(patch: Partial<UserPrefs>): Promise<UserPrefs> {
  return apiFetch('/me/prefs', { method: 'PATCH', body: JSON.stringify(patch) });
}

/**
 * Zgłasza, co ta przeglądarka umie zdekodować.
 *
 * Serwer używa tego, żeby nie przekodowywać materiału, który u tej osoby i tak by
 * się odtworzył. Bez raportu decyduje najostrożniej z możliwych.
 */
export function reportCapabilities(input: {
  hevc: boolean;
  av1: boolean;
  matroska: boolean;
}): Promise<{ hevc: boolean; av1: boolean; matroska: boolean; reportedAt: string }> {
  return apiFetch('/media/capabilities', { method: 'POST', body: JSON.stringify(input) });
}

/* ------------------------- pobieranie na dysk widza ------------------------- */

export interface DownloadableEpisode {
  mediaFileId: string;
  episodeNumber: number | null;
  /** Rozszerzenie materiału bez kropki: `mp4`, `mkv`, `webm`. */
  extension: string;
  sizeBytes: number | null;
  durationSec: number | null;
  subtitles: Array<{ id: string; lang: string; format: string; label: string | null }>;
}

export interface DownloadManifest {
  titleId: string;
  titleName: string;
  episodes: DownloadableEpisode[];
}

/** Co z tej serii leży u nas gotowe do zabrania. */
export function getDownloadManifest(titleId: string): Promise<DownloadManifest> {
  return apiFetch<DownloadManifest>(`/downloads/titles/${titleId}/manifest`);
}

/**
 * Adresy pobierania budujemy, a nie pobieramy przez `fetch`.
 *
 * Plik po czterysta megabajtów ma iść przez **menedżer pobierania przeglądarki**:
 * on pokazuje postęp, wznawia, zapisuje tam, gdzie widz chce, i nie trzyma tego
 * w pamięci karty. `fetch` plus `blob:` załadowałby całą serię do RAM-u, żeby potem
 * ją zapisać — przy dwudziestu gigabajtach zabiłby kartę.
 */
export function episodeDownloadUrl(mediaFileId: string, seasonNumber?: number | null): string {
  const suffix = seasonNumber != null ? `?season=${seasonNumber}` : '';
  return `/api/v1/downloads/episode/${mediaFileId}${suffix}`;
}

export function packDownloadUrl(input: {
  titleId: string;
  /** Puste znaczy „wszystko, co mamy". */
  episodes: number[];
  withSubtitles: boolean;
  /**
   * Numer sezonu do nazw `SxxExx` albo `null` dla `Exx`.
   *
   * Podaje go widz, bo katalog go nie zna — AniList ma porę roku i rok, nie numer
   * sezonu w serii.
   */
  seasonNumber?: number | null;
  /**
   * Dograć brakujące napisy z animesub.info przed spakowaniem.
   *
   * Domyślnie nie: uzupełnianie wysyła do obcego serwisu po dwa żądania na odcinek,
   * więc dzieje się na wyraźne życzenie, a nie przy każdym „pobierz serię".
   */
  fillSubtitles?: boolean;
}): string {
  const query = new URLSearchParams({ titleId: input.titleId });
  if (input.episodes.length > 0) query.set('episodes', input.episodes.join(','));
  query.set('subtitles', input.withSubtitles ? '1' : '0');
  if (input.seasonNumber != null) query.set('season', String(input.seasonNumber));
  if (input.fillSubtitles === true) query.set('fill', '1');
  return `/api/v1/downloads/pack?${query.toString()}`;
}

/* ------------------------ napisy z animesub.info ------------------------ */

export interface AnimesubCandidate {
  id: string;
  sh: string;
  title: string;
  episode: number | null;
  author: string | null;
  addedAt: string | null;
  format: string | null;
  /** Pod jakie wydanie autor zsynchronizował napisy — po tym się wybiera. */
  syncedTo: string | null;
  fontsUrl: string | null;
  downloads: number | null;
  matchesEpisode: boolean;
}

/** Kandydaci z animesuba dla tytułu i odcinka. Jedno żądanie na kliknięcie. */
export function searchAnimesub(
  titleId: string,
  episode: number,
): Promise<{ query: string; candidates: AnimesubCandidate[] }> {
  return apiFetch(`/subtitles/animesub/search?titleId=${titleId}&episode=${episode}`);
}

/** Pobiera wskazany wpis i dowiązuje go do materiału jako nową ścieżkę napisów. */
export function fetchAnimesub(input: {
  mediaFileId: string;
  titleId: string;
  episodeNumber: number;
  entryId: string;
}): Promise<{ trackId: string; label: string }> {
  return apiFetch('/subtitles/animesub/fetch', { method: 'POST', body: JSON.stringify(input) });
}
